import { HttpErrorResponse } from '@angular/common/http'
import { ErrorHandler, Injectable, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular'
import { environment } from '../../../environments/environment'
import { NETWORK_ERROR_CODE } from '../interceptor/errorTranslation.interceptor'
import { ChunkLoadErrorService } from '../services/chunk-load-error.service'
import { PosthogService } from '../services/posthog.service'
import { SharedService } from '../../shared/services/shared.service'
import { isIgnoredError } from './ignored-error'

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private sharedService = inject(SharedService)
  private chunkLoadErrorService = inject(ChunkLoadErrorService)
  private posthog = inject(PosthogService)
  private sentryErrorHandler = environment.production ? Sentry.createErrorHandler() : null
  private translate = inject(TranslateService)

  handleError(error: any): void {
    if (isIgnoredError(error)) return

    if (this.chunkLoadErrorService.isChunkLoadError(error)) {
      this.chunkLoadErrorService.handleChunkLoadError()
      return
    }

    // Ignore 402 Payment Required errors
    if (error?.status === 402 || error?.rejection?.status === 402) {
      return
    }

    // TT-460: ~116 HTTP subscribes across the app have no error callback, so a
    // failed request just silently vanishes — no toast, a spinner left
    // spinning forever. An HTTP error with no error callback propagates here
    // (as-is, or wrapped in `rejection` when it surfaces through a promise).
    // The other ~378 subscribes that DO handle their own error already show
    // their own toast and never reach this handler, so this can't double-toast
    // them — it only ever fires for the ones nobody was already telling the
    // user about.
    const httpError = this.extractHttpError(error)
    if (httpError) {
      // A request that never reached the API already carries its translated
      // "you're offline" body from errorTranslationInterceptor — that is the
      // one thing the user can act on, so don't bury it under the generic.
      const isNetworkError = httpError.error?._originalCode === NETWORK_ERROR_CODE
      this.sharedService.showError(isNetworkError ? httpError.error.message : this.translate.instant('common.somethingWentWrong'))
    }

    // A failed API call is not a crash: the merchant was already told (toast
    // above or at the call site) and the backend has it. Everything else is an
    // error in our own code, and PostHog had recorded none in 30 days — a crash
    // in the mobile media upload would have been invisible.
    if (!httpError) {
      this.posthog.captureException(error?.rejection instanceof Error ? error.rejection : error)
    }

    // For production, use Sentry's error handler; otherwise just log to console
    if (this.sentryErrorHandler) {
      this.sentryErrorHandler.handleError(error)
    } else {
      console.error(error)
    }
  }

  private extractHttpError(error: any): HttpErrorResponse | null {
    if (error instanceof HttpErrorResponse) return error
    if (error?.rejection instanceof HttpErrorResponse) return error.rejection
    return null
  }
}
